import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus,
  PlusSquare,
  Search,
  Loader2,
  Sticker,
  UserPlus,
  UserMinus,
  ArrowLeftRight,
  Printer,
  MoreHorizontal,
  ExternalLink,
  Trash2,
  Copy,
  PackageCheck,
  Cpu,
  Sparkles,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import KpiCard from "@/components/KpiCard";

const STATUS_LABELS = {
  free: { label: "Frei", cls: "border-white/10 text-muted-foreground bg-white/[0.03]" },
  assigned: { label: "Zugewiesen", cls: "border-sky-500/30 text-sky-400 bg-sky-500/10" },
  preparing: { label: "In Vorbereitung", cls: "border-amber-500/30 text-amber-400 bg-amber-500/10" },
  ready: { label: "Verkaufsbereit", cls: "border-primary/30 text-primary bg-primary/10" },
  delivered: { label: "Ausgeliefert", cls: "border-emerald-500/30 text-emerald-400 bg-emerald-500/10" },
  disabled: { label: "Deaktiviert", cls: "border-destructive/40 text-destructive bg-destructive/10" },
};

function StatusBadge({ status }) {
  const l = STATUS_LABELS[status] || STATUS_LABELS.free;
  return (
    <Badge variant="outline" className={l.cls} data-testid={`stand-status-${status}`}>
      {l.label}
    </Badge>
  );
}

export default function StandsPage() {
  const [stands, setStands] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [customers, setCustomers] = useState([]);
  const [batchOpen, setBatchOpen] = useState(false);
  const [assignFor, setAssignFor] = useState(null); // stand object or null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search };
      if (status !== "all") params.status = status;
      const { data } = await api.get("/stands", { params });
      setStands(data.stands || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    // Preload customer list once so assign dialogs are instant.
    api
      .get("/customers")
      .then(({ data }) => setCustomers(data.customers || []))
      .catch(() => setCustomers([]));
  }, []);

  const createOne = async () => {
    try {
      const { data } = await api.post("/stands", {});
      toast.success(`Aufsteller ${data.serial_number} angelegt.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeStand = async (s) => {
    try {
      await api.delete(`/stands/${s.id}`);
      toast.success(`${s.serial_number} gelöscht.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyLink = async (s) => {
    try {
      await navigator.clipboard.writeText(s.nfc_link);
      toast.success("Stand-URL kopiert.");
    } catch {
      toast.error("Kopieren fehlgeschlagen.");
    }
  };

  const unassign = async (s) => {
    try {
      await api.post(`/stands/${s.id}/unassign`);
      toast.success(`${s.serial_number} freigegeben.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const kpiCards = useMemo(
    () => [
      { label: "Frei", value: counts.free ?? 0, icon: Sticker, tone: "muted", testId: "kpi-stand-free" },
      { label: "In Vorbereitung", value: counts.preparing ?? 0, icon: Cpu, tone: "warning", testId: "kpi-stand-preparing" },
      { label: "Verkaufsbereit", value: counts.ready ?? 0, icon: Sparkles, tone: "primary", testId: "kpi-stand-ready" },
      { label: "Ausgeliefert", value: counts.delivered ?? 0, icon: PackageCheck, tone: "success", testId: "kpi-stand-delivered" },
    ],
    [counts]
  );

  return (
    <div className="space-y-6" data-testid="stands-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="label-eyebrow">Aufsteller</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">
            Physische Aufsteller
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Zentrales Inventar aller Bewertungs-Aufsteller. Serial &amp; Stand-Code sind unveränderlich.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() => setBatchOpen(true)}
            className="rounded-full h-11 px-4 gap-2"
            data-testid="stands-batch-btn"
          >
            <PlusSquare className="w-4 h-4" /> Batch anlegen
          </Button>
          <Button
            onClick={createOne}
            className="rounded-full h-11 px-5 gap-2"
            data-testid="stands-create-btn"
          >
            <Plus className="w-4 h-4" /> Neuer Aufsteller
          </Button>
        </div>
      </header>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((k) => (
          <KpiCard key={k.label} {...k} />
        ))}
      </section>

      <div className="card-surface p-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="stands-search-input"
            placeholder="Seriennummer, Stand-Code oder Notiz suchen…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-11 bg-background/50"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger
            className="h-11 sm:w-56 bg-background/50"
            data-testid="stands-status-filter"
          >
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Status</SelectItem>
            <SelectItem value="free">Frei</SelectItem>
            <SelectItem value="assigned">Zugewiesen</SelectItem>
            <SelectItem value="preparing">In Vorbereitung</SelectItem>
            <SelectItem value="ready">Verkaufsbereit</SelectItem>
            <SelectItem value="delivered">Ausgeliefert</SelectItem>
            <SelectItem value="disabled">Deaktiviert</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block card-surface overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/[0.06] hover:bg-transparent">
              <TableHead className="label-eyebrow">Serial</TableHead>
              <TableHead className="label-eyebrow">Stand-Code</TableHead>
              <TableHead className="label-eyebrow">Kunde</TableHead>
              <TableHead className="label-eyebrow">Status</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="py-16 text-center">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Lädt…
                </TableCell>
              </TableRow>
            ) : stands.length ? (
              stands.map((s) => (
                <TableRow
                  key={s.id}
                  data-testid={`stand-row-${s.id}`}
                  className="border-white/[0.05] hover:bg-white/[0.02]"
                >
                  <TableCell className="align-middle">
                    <Link
                      to={`/aufsteller/${s.id}`}
                      className="font-mono font-semibold hover:text-primary"
                    >
                      {s.serial_number}
                    </Link>
                  </TableCell>
                  <TableCell className="font-mono text-sm text-muted-foreground">
                    /t/{s.stand_code}
                  </TableCell>
                  <TableCell>
                    {s.customer ? (
                      <Link
                        to={`/kunden/${s.customer.id}`}
                        className="hover:text-primary"
                      >
                        {s.customer.business_name}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground text-sm">— frei —</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={s.status} />
                  </TableCell>
                  <TableCell className="text-right">
                    <StandRowActions
                      stand={s}
                      onAssign={() => setAssignFor(s)}
                      onReassign={() => setAssignFor(s)}
                      onUnassign={() => unassign(s)}
                      onCopy={() => copyLink(s)}
                      onDelete={() => removeStand(s)}
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="py-16 text-center text-muted-foreground">
                  Keine Aufsteller gefunden.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {loading && (
          <div className="card-surface p-6 text-center text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Lädt…
          </div>
        )}
        {!loading &&
          stands.map((s) => (
            <div key={s.id} className="card-surface p-4" data-testid={`stand-card-${s.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Link
                    to={`/aufsteller/${s.id}`}
                    className="font-mono font-semibold hover:text-primary"
                  >
                    {s.serial_number}
                  </Link>
                  <div className="text-xs text-muted-foreground truncate font-mono">
                    /t/{s.stand_code}
                  </div>
                  <div className="text-sm mt-1 truncate">
                    {s.customer ? s.customer.business_name : (
                      <span className="text-muted-foreground">— frei —</span>
                    )}
                  </div>
                </div>
                <StatusBadge status={s.status} />
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {s.customer_id ? (
                  <>
                    <Button
                      size="sm"
                      variant="secondary"
                      className="rounded-full gap-1.5"
                      onClick={() => setAssignFor(s)}
                      data-testid={`stand-reassign-btn-${s.id}`}
                    >
                      <ArrowLeftRight className="w-3.5 h-3.5" /> Neu zuweisen
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="rounded-full gap-1.5"
                      onClick={() => unassign(s)}
                      data-testid={`stand-unassign-btn-${s.id}`}
                    >
                      <UserMinus className="w-3.5 h-3.5" /> Freigeben
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    className="rounded-full gap-1.5"
                    onClick={() => setAssignFor(s)}
                    data-testid={`stand-assign-btn-${s.id}`}
                  >
                    <UserPlus className="w-3.5 h-3.5" /> Zuweisen
                  </Button>
                )}
                <Link to={`/aufsteller/${s.id}`}>
                  <Button size="sm" variant="ghost" className="rounded-full gap-1.5">
                    <ExternalLink className="w-3.5 h-3.5" /> Details
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        {!loading && !stands.length && (
          <div className="card-surface p-8 text-center text-sm text-muted-foreground">
            Keine Aufsteller gefunden.
          </div>
        )}
      </div>

      <BatchDialog
        open={batchOpen}
        onOpenChange={setBatchOpen}
        onDone={() => {
          setBatchOpen(false);
          load();
        }}
      />

      <AssignDialog
        stand={assignFor}
        customers={customers}
        onClose={() => setAssignFor(null)}
        onDone={() => {
          setAssignFor(null);
          load();
        }}
      />
    </div>
  );
}

function StandRowActions({ stand, onAssign, onReassign, onUnassign, onCopy, onDelete }) {
  const isAssigned = !!stand.customer_id;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8"
          data-testid={`stand-actions-${stand.id}`}
        >
          <MoreHorizontal className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 bg-popover">
        <DropdownMenuItem asChild data-testid={`stand-action-open-${stand.id}`}>
          <Link to={`/aufsteller/${stand.id}`}>
            <ExternalLink className="w-4 h-4 mr-2" /> Öffnen
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild data-testid={`stand-action-label-${stand.id}`}>
          <Link to={`/aufsteller/${stand.id}/label`}>
            <Printer className="w-4 h-4 mr-2" /> Label drucken
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onCopy} data-testid={`stand-action-copy-${stand.id}`}>
          <Copy className="w-4 h-4 mr-2" /> URL kopieren
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {isAssigned ? (
          <>
            <DropdownMenuItem onClick={onReassign} data-testid={`stand-action-reassign-${stand.id}`}>
              <ArrowLeftRight className="w-4 h-4 mr-2" /> Neu zuweisen
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onUnassign} data-testid={`stand-action-unassign-${stand.id}`}>
              <UserMinus className="w-4 h-4 mr-2" /> Freigeben
            </DropdownMenuItem>
          </>
        ) : (
          <>
            <DropdownMenuItem onClick={onAssign} data-testid={`stand-action-assign-${stand.id}`}>
              <UserPlus className="w-4 h-4 mr-2" /> Zuweisen
            </DropdownMenuItem>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <DropdownMenuItem
                  onSelect={(e) => e.preventDefault()}
                  className="text-destructive focus:text-destructive"
                  data-testid={`stand-action-delete-${stand.id}`}
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Löschen
                </DropdownMenuItem>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Aufsteller löschen?</AlertDialogTitle>
                  <AlertDialogDescription>
                    <strong>{stand.serial_number}</strong> wird endgültig entfernt. Nur freie
                    Aufsteller können gelöscht werden.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={onDelete}
                    className="bg-destructive hover:bg-destructive/90"
                    data-testid={`stand-confirm-delete-${stand.id}`}
                  >
                    Löschen
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function BatchDialog({ open, onOpenChange, onDone }) {
  const [count, setCount] = useState(10);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/stands/batch", { count: Number(count) });
      toast.success(`${data.created} neue Aufsteller angelegt.`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="batch-dialog">
        <DialogHeader>
          <DialogTitle>Batch anlegen</DialogTitle>
          <DialogDescription>
            Erstelle mehrere freie Aufsteller auf einmal. Serial-Nummern werden fortlaufend
            vergeben. Maximal 100 pro Batch.
          </DialogDescription>
        </DialogHeader>
        <div>
          <label className="label-eyebrow">Anzahl</label>
          <Input
            type="number"
            min={1}
            max={100}
            value={count}
            onChange={(e) => setCount(e.target.value)}
            className="mt-1 h-11 bg-background/50"
            data-testid="batch-count-input"
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !count || count < 1}
            className="rounded-full gap-2"
            data-testid="batch-submit-btn"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {count} anlegen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignDialog({ stand, customers, onClose, onDone }) {
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);
  const isReassign = !!stand?.customer_id;

  useEffect(() => {
    setSelected("");
  }, [stand]);

  const submit = async () => {
    if (!selected || !stand) return;
    setBusy(true);
    try {
      const path = isReassign ? "reassign" : "assign";
      await api.post(`/stands/${stand.id}/${path}`, { customer_id: selected });
      toast.success(
        isReassign
          ? `${stand.serial_number} neu zugewiesen.`
          : `${stand.serial_number} zugewiesen.`
      );
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!stand} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="assign-dialog">
        <DialogHeader>
          <DialogTitle>
            {isReassign ? "Neu zuweisen" : "Aufsteller zuweisen"} — {stand?.serial_number}
          </DialogTitle>
          <DialogDescription>
            Der Aufsteller bekommt die Google-Bewertung des ausgewählten Kunden.
            Serial-Nummer und Stand-Code bleiben unverändert — das gedruckte Label muss
            nicht neu erstellt werden.
          </DialogDescription>
        </DialogHeader>
        <div>
          <label className="label-eyebrow">Kunde</label>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger
              className="mt-1 h-11 bg-background/50"
              data-testid="assign-customer-select"
            >
              <SelectValue placeholder="Kunde auswählen…" />
            </SelectTrigger>
            <SelectContent>
              {customers.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.business_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isReassign && stand?.customer && (
            <p className="text-xs text-amber-400 mt-2">
              Aktuell zugewiesen: {stand.customer.business_name}. Die neue Zuweisung setzt
              die Onboarding-Checkliste zurück.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            onClick={submit}
            disabled={!selected || busy}
            className="rounded-full gap-2"
            data-testid="assign-submit-btn"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {isReassign ? "Neu zuweisen" : "Zuweisen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
